/*
 * TEEN SELF-HOLDER — the Get ready card's "Parent/guardian signature needed"
 * panel (VITE_TEEN_SELF_HOLDER_ENABLED; design MY-DESIGN-JUNIOR-SELF-HOLDER-
 * 1020 §4.3.2 / §4.7; my-api PR1 routes).
 *
 *   Ask      — parent name, email and relationship → POST guardian-request.
 *   Waiting  — "Waiting for {parent} to sign" + Change email (PUT, 3 max) and
 *              Resend (1 per 24 h). The 409 / 429 caps read in plain language.
 *   Signed   — no panel (the step row reads "Signed by {parent}").
 *
 * The parent's email is sent to my-api and never stored on this device or
 * echoed back; only the parent's NAME is kept (teenGuardian.ts) for the
 * waiting line. Every success re-reads readiness (bumpReadinessRefresh).
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import { loadSession } from '../auth/session'
import { bumpReadinessRefresh } from '../data/getReady'
import {
  changeGuardianRequest, myApiBase, MyApiError, requestGuardianSignature, resendGuardianRequest,
  type GuardianRequestInput,
} from '../data/myApi'
import { guardianRequestErrorCopy, recordTeenGuardianRequest, type TeenGuardianCard } from '../data/teenGuardian'
import { useLocale } from '../i18n/useLocale'
import { EmailHiddenCharNotice, useCleanEmail } from '../components/EmailHiddenCharNotice'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const GUARDIAN_RELATIONSHIPS = ['parent', 'legal_guardian'] as const
export type GuardianRelationship = (typeof GUARDIAN_RELATIONSHIPS)[number]

const inputStyle: CSSProperties = {
  width: '100%', minHeight: 44, borderRadius: 10, padding: '0 12px', fontSize: 14, boxSizing: 'border-box',
  background: 'var(--tru-surface-1)', color: 'var(--tru-text-primary)', border: '1px solid var(--tru-border-14)',
  fontFamily: 'inherit', outline: 'none',
}
const lblStyle: CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--tru-text-secondary)', display: 'block', margin: '10px 0 5px' }
const primaryBtn = (ok: boolean): CSSProperties => ({
  minHeight: 42, borderRadius: 10, border: 0, padding: '0 16px', fontSize: 13, fontWeight: 800, fontFamily: 'inherit',
  background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)', ...(ok ? { cursor: 'pointer' } : { opacity: 0.4, cursor: 'not-allowed' }),
})
const outlineBtn: CSSProperties = {
  minHeight: 40, borderRadius: 10, border: '1px solid var(--tru-border-16)', background: 'transparent', padding: '0 14px',
  color: '#c3c9c5', fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
}

type Mode = 'ask' | 'change' | 'waiting'

export function TeenGuardianPanel({ card }: { card: TeenGuardianCard }) {
  const { t } = useLocale()
  const location = useLocation()
  const [mode, setMode] = useState<Mode>(card.state === 'waiting' ? 'waiting' : 'ask')
  const [parentName, setParentName] = useState(card.parentName)
  const [expired, setExpired] = useState(!!card.expired)
  const [name, setName] = useState('')
  const [email, setEmail, emailHidden] = useCleanEmail()
  const [relationship, setRelationship] = useState<GuardianRelationship | ''>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Server state moved (a readiness re-read) → follow it.
  useEffect(() => {
    setMode(card.state === 'waiting' ? 'waiting' : 'ask')
    setParentName(card.parentName)
    setExpired(!!card.expired)
  }, [card.state, card.parentName, card.expired])

  // WaiverFlow's 422 next:'guardian_request' hand-off lands here (route state).
  const focusMe = (location.state as { guardianAskTicket?: string } | null)?.guardianAskTicket === card.ticketId
  useEffect(() => {
    if (!focusMe) return
    try { rootRef.current?.scrollIntoView?.({ block: 'center' }) } catch { /* jsdom / old browsers */ }
  }, [focusMe])

  if (card.state === 'signed') return null

  const formOk = name.trim() !== '' && EMAIL_RE.test(email.trim()) && relationship !== ''

  const run = async (fn: (idToken: string, base: string) => Promise<void>) => {
    const base = myApiBase()
    const session = loadSession()
    if (!base || !session) {
      setError(t('teenGuardian.error.unavailable'))
      return
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fn(session.idToken, base)
    } catch (e) {
      if (e instanceof MyApiError && e.code === 'guardian_request_exists') {
        // Another device (or an earlier visit) already asked: show the
        // waiting state; Change email stays available.
        recordTeenGuardianRequest(card.ticketId, { state: 'requested' })
        setMode('waiting')
        setNotice(t('teenGuardian.error.exists'))
        bumpReadinessRefresh()
      } else if (e instanceof MyApiError && e.code === 'already_signed') {
        recordTeenGuardianRequest(card.ticketId, { state: 'signed' })
        setNotice(t('teenGuardian.error.alreadySigned'))
        bumpReadinessRefresh()
      } else if (e instanceof MyApiError && (e.code === 'guardian_request_expired' || e.code === 'guardian_request_not_found')) {
        setExpired(e.code === 'guardian_request_expired')
        setMode('ask')
        setError(guardianRequestErrorCopy(e, t))
      } else {
        setError(guardianRequestErrorCopy(e, t))
      }
    } finally {
      setBusy(false)
    }
  }

  const input = (): GuardianRequestInput => ({ guardianName: name.trim(), guardianEmail: email.trim(), relationship })

  const submitForm = () => {
    if (!formOk || busy) return
    const typedName = name.trim()
    void run(async (idToken, base) => {
      const view = mode === 'change'
        ? await changeGuardianRequest(idToken, base, card.ticketId, input())
        : await requestGuardianSignature(idToken, base, card.ticketId, input())
      recordTeenGuardianRequest(card.ticketId, view, typedName)
      setParentName(typedName)
      setExpired(false)
      setMode('waiting')
      setName('')
      setEmail('')
      setRelationship('')
      setNotice(t(mode === 'change' ? 'teenGuardian.changed' : 'teenGuardian.sent', { parent: typedName }))
      bumpReadinessRefresh()
    })
  }

  const resend = () => {
    if (busy) return
    void run(async (idToken, base) => {
      const view = await resendGuardianRequest(idToken, base, card.ticketId)
      recordTeenGuardianRequest(card.ticketId, view)
      setNotice(t('teenGuardian.resent'))
    })
  }

  const form = mode === 'ask' || mode === 'change'

  return (
    <div
      ref={rootRef}
      id={`getready.guardian.${card.ticketId}`}
      data-testid="teen-guardian-panel"
      data-state={form ? mode : 'waiting'}
      style={{ padding: '12px 16px 14px', borderBlockStart: '1px solid var(--tru-hairline-06)' }}
    >
      {form ? (
        <div data-testid="teen-guardian-form">
          <div style={{ fontSize: 13, fontWeight: 800 }}>
            {mode === 'change' ? t('teenGuardian.change.title') : t('teenGuardian.ask.title')}
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', lineHeight: 1.5, margin: '4px 0 0' }}>
            {expired && mode === 'ask' ? t('teenGuardian.ask.expired') : t('teenGuardian.ask.body')}
          </p>
          <label style={lblStyle} htmlFor={`tg-name-${card.ticketId}`}>{t('teenGuardian.field.name')}</label>
          <input id={`tg-name-${card.ticketId}`} data-testid="teen-guardian-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" style={inputStyle} />
          <label style={lblStyle} htmlFor={`tg-email-${card.ticketId}`}>{t('teenGuardian.field.email')}</label>
          <input id={`tg-email-${card.ticketId}`} data-testid="teen-guardian-email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" style={inputStyle} />
          <EmailHiddenCharNotice show={emailHidden} testid="teen-guardian-email-hidden-char" />
          <label style={lblStyle} htmlFor={`tg-rel-${card.ticketId}`}>{t('teenGuardian.field.relationship')}</label>
          <select
            id={`tg-rel-${card.ticketId}`}
            data-testid="teen-guardian-relationship"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as GuardianRelationship | '')}
            style={inputStyle}
          >
            <option value="">{t('teenGuardian.field.relationship.choose')}</option>
            <option value="parent">{t('teenGuardian.relationship.parent')}</option>
            <option value="legal_guardian">{t('teenGuardian.relationship.legal_guardian')}</option>
          </select>
          <div style={{ display: 'flex', gap: 8, marginBlockStart: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="teen-guardian-submit"
              onClick={submitForm}
              aria-disabled={!formOk || busy}
              style={primaryBtn(formOk && !busy)}
            >
              {busy ? t('teenGuardian.sending') : mode === 'change' ? t('teenGuardian.change.submit') : t('teenGuardian.ask.submit')}
            </button>
            {mode === 'change' && (
              <button type="button" data-testid="teen-guardian-cancel" onClick={() => { setMode('waiting'); setError(null) }} style={outlineBtn}>
                {t('teenGuardian.change.cancel')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div data-testid="teen-guardian-waiting">
          <div role="status" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--tru-text-secondary)', lineHeight: 1.45 }}>
            {parentName ? t('teenGuardian.waiting.body', { parent: parentName }) : t('teenGuardian.waiting.bodyNoName')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBlockStart: 10, flexWrap: 'wrap' }}>
            <button type="button" data-testid="teen-guardian-change" onClick={() => { setMode('change'); setError(null); setNotice(null) }} style={outlineBtn}>
              {t('teenGuardian.waiting.change')}
            </button>
            <button type="button" data-testid="teen-guardian-resend" onClick={resend} aria-disabled={busy} style={outlineBtn}>
              {t('teenGuardian.waiting.resend')}
            </button>
          </div>
        </div>
      )}
      {notice && !error && (
        <div data-testid="teen-guardian-notice" role="status" style={{ fontSize: 11.5, color: 'var(--tru-status-done-text)', fontWeight: 700, marginBlockStart: 10 }}>{notice}</div>
      )}
      {error && (
        <div data-testid="teen-guardian-error" role="alert" style={{ fontSize: 11.5, color: 'var(--tru-status-error)', fontWeight: 700, marginBlockStart: 10, lineHeight: 1.45 }}>{error}</div>
      )}
    </div>
  )
}
